import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faCalendarCheck, faClock, faExclamationTriangle, faChartLine,
  faCheckCircle, faSpinner, faSync, faFilter, faTimes, faBuilding
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ---- Cache Dexie (24h) ----
const db = new Dexie('PresenceDashboardCache');
db.version(1).stores({ stats: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;
async function getCachedStats(key) {
  const entry = await db.stats.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_DURATION) return entry.data;
  return null;
}
async function setCachedStats(key, data) {
  await db.stats.put({ key, data, timestamp: Date.now() });
}

const API_URL = `${API_CONFIG2.apiUrl}presence/api_presence_stats.php`;
const API_EMPLOYE_FILTERS = `${API_CONFIG2.apiUrl}employe/api_filters.php`; // à créer

// Utilitaire fetch
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) throw new Error(`HTTP ${response.status} : ${text}`);
  try { return JSON.parse(text); } catch { throw new Error('JSON invalide'); }
};

const PresenceDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  // Filtres
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7*24*60*60*1000).toISOString().slice(0,10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0,10));
  const [service, setService] = useState('');
  const [department, setDepartment] = useState('');
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState([]);

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);
  const fetchTimeout = useRef(null);

  // Charger la liste des services/départements pour les filtres
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const data = await fetchJSON(`${API_EMPLOYE_FILTERS}?action=services_departments`);
        if (data.status === 'success') {
          setServices(data.services || []);
          setDepartments(data.departments || []);
        }
      } catch (e) { console.warn('Erreur chargement filtres', e); }
    };
    loadFilters();
  }, []);

  // Fonction de fetch avec paramètres
  const fetchStats = useCallback(async (forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        service,
        department,
        unique: new Date().getTime()
      });
      const cacheKey = `stats_${startDate}_${endDate}_${service}_${department}`;
      
      if (!forceRefresh) {
        const cached = await getCachedStats(cacheKey);
        if (cached) {
          setStats(cached);
          setDataSource('cache');
          setLoading(false);
          return;
        }
      }

      const url = `${API_URL}?${params}`;
      const data = await fetchJSON(url, { signal: controller.signal });
      if (data.status === 'success') {
        setStats(data);
        setDataSource('live');
        await setCachedStats(cacheKey, data);
        setLoading(false);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      setLoading(false);
    }
  }, [startDate, endDate, service, department]);

  // Rafraîchissement
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      setRefreshing(true);
      fetchStats(true).finally(() => setRefreshing(false));
    }
  };

  // Appliquer les filtres (déclenché manuellement)
  const applyFilters = () => {
    fetchStats();
  };

  // Chargement initial
  useEffect(() => {
    fetchStats();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
    };
  }, []);

  // Effet pour réappliquer quand les filtres changent (avec debounce)
  useEffect(() => {
    if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      fetchStats();
    }, 300);
    return () => clearTimeout(fetchTimeout.current);
  }, [startDate, endDate, service, department]);

  if (loading && !stats) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <p className="text-secondary">Chargement des statistiques...</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="text-center py-5 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-3" />
        <p>{error}</p>
        <button className="monetra-btn primary" onClick={handleRefresh}>
          <FontAwesomeIcon icon={faSync} /> Réessayer
        </button>
      </div>
    );
  }

  if (!stats) return <p className="text-center text-secondary">Aucune donnée.</p>;

  // Préparation des données graphiques
  const donutLabels = stats.byStatus.map(item => item.statut === 'P' ? 'Présent' : item.statut === 'R' ? 'Retard' : 'Absent');
  const donutData = {
    labels: donutLabels,
    datasets: [{
      data: stats.byStatus.map(item => item.count),
      backgroundColor: ['#3b82f6', '#f59e0b', '#ef4444'],
      borderColor: '#0b1428',
      borderWidth: 2,
      hoverOffset: 10,
    }]
  };
  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#e0e7ff', boxWidth: 12, usePointStyle: true, padding: 16 }, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          }
        }
      }
    },
    cutout: '70%',
  };

  const barLabels = stats.evolution.map(d => d.date);
  const barData = {
    labels: barLabels,
    datasets: [
      { label: 'Présent', data: stats.evolution.map(d => d.Présent), backgroundColor: '#3b82f6', borderRadius: 4 },
      { label: 'Retard', data: stats.evolution.map(d => d.Retard), backgroundColor: '#f59e0b', borderRadius: 4 },
      { label: 'Absent', data: stats.evolution.map(d => d.Absent), backgroundColor: '#ef4444', borderRadius: 4 }
    ]
  };
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#e0e7ff', boxWidth: 12, usePointStyle: true, padding: 16 } }
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: '#94a3b8' } },
      y: { stacked: true, grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#94a3b8' } }
    }
  };

  // Graphique par service (si disponible)
  const serviceLabels = stats.byService ? stats.byService.map(s => s.service) : [];
  const serviceData = stats.byService ? {
    labels: serviceLabels,
    datasets: [
      { label: 'Présent', data: stats.byService.map(s => s.P), backgroundColor: '#3b82f6' },
      { label: 'Retard', data: stats.byService.map(s => s.R), backgroundColor: '#f59e0b' },
      { label: 'Absent', data: stats.byService.map(s => s.A), backgroundColor: '#ef4444' }
    ]
  } : null;

  const serviceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#e0e7ff', boxWidth: 12, usePointStyle: true, padding: 16 } }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#94a3b8' } },
      y: { stacked: true, grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#94a3b8' } }
    }
  };

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }
        .dashboard-presence {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .monetra-btn {
          padding: 8px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live { background: rgba(46,204,113,0.15); color: #2ecc71; border-color: rgba(46,204,113,0.12); }
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(clamp(140px, 20vw, 200px), 1fr));
          gap: clamp(12px, 2vw, 20px);
          margin-bottom: 20px;
        }
        .kpi-item {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 20px;
          padding: clamp(1rem, 1.5vw, 1.2rem);
          text-align: center;
          transition: all 0.3s ease;
        }
        .kpi-item:hover { transform: translateY(-3px); border-color: rgba(59, 130, 246, 0.2); box-shadow: 0 0 30px rgba(59, 130, 246, 0.08); }
        .kpi-icon { font-size: clamp(1.2rem, 2vw, 1.5rem); color: var(--text-secondary); margin-bottom: 6px; display: block; }
        .kpi-value { font-size: clamp(1.6rem, 3vw, 2rem); font-weight: 700; background: linear-gradient(135deg, #fff, var(--accent-blue)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .kpi-label { color: var(--text-secondary); font-size: clamp(0.75rem, 1vw, 0.85rem); font-weight: 500; }
        .kpi-sub { color: var(--text-muted); font-size: clamp(0.65rem, 0.8vw, 0.75rem); }
        .charts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(12px, 2vw, 20px); }
        .chart-box { height: clamp(180px, 35vh, 220px); position: relative; }
        @media (max-width: 768px) { .charts-grid { grid-template-columns: 1fr; } .chart-box { height: 200px; } }
        .filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }
        .filter-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: var(--text-primary);
          padding: 8px 14px;
          outline: none;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          min-height: 40px;
        }
        .filter-input:focus { border-color: var(--accent-blue); }
        .filter-input option { background: #0b1428; }
        .progress-section { margin-top: 1.5rem; }
        .progress-item { margin-bottom: 1rem; }
        .progress-header { display: flex; justify-content: space-between; font-size: clamp(0.8rem, 1vw, 0.9rem); color: var(--text-secondary); }
        .progress-track { width: 100%; height: 6px; background: rgba(255,255,255,0.06); border-radius: 20px; overflow: hidden; margin-top: 4px; }
        .progress-fill { height: 100%; border-radius: 20px; transition: width 1.8s cubic-bezier(0.2, 0.8, 0.2, 1); }
        .progress-fill.blue { background: linear-gradient(90deg, var(--accent-blue), var(--accent-dark-blue)); }
        .progress-fill.orange { background: linear-gradient(90deg, #f59e0b, #d97706); }
        .progress-fill.red { background: linear-gradient(90deg, #ef4444, #dc2626); }
        .text-secondary { color: var(--text-secondary); }
        .text-danger { color: #ef4444; }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .display-6 { font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: 700; }
        .m-0 { margin: 0; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 1rem; }
        .mb-4 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .me-2 { margin-right: 0.5rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .gap-3 { gap: 1rem; }
        .gap-2 { gap: 0.5rem; }
        .mt-2 { margin-top: 0.5rem; }
        @media (max-width: 480px) {
          .kpi-grid { grid-template-columns: 1fr 1fr; }
          .kpi-item { padding: 0.8rem; }
          .kpi-value { font-size: 1.4rem; }
          .chart-box { height: 160px; }
        }
      `}</style>

      <div className="dashboard-presence">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">Tableau de bord des présences</h1>
            <p className="text-secondary m-0">Analyse sur la période du {startDate} au {endDate}</p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' : dataSource === 'live' ? '🔄 Live' : '📦 Cache'}
            </span>
            <button className="monetra-btn primary" onClick={handleRefresh} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSync} spin={loading || refreshing} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="glass-card">
          <div className="filter-bar">
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faFilter} className="text-secondary" />
              <span className="text-secondary">Filtres :</span>
            </div>
            <input type="date" className="filter-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
            <span className="text-secondary">→</span>
            <input type="date" className="filter-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
         
            {(service || department) && (
              <button className="monetra-btn" onClick={() => { setService(''); setDepartment(''); }}>
                <FontAwesomeIcon icon={faTimes} /> Effacer
              </button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="glass-card">
          <div className="kpi-grid">
            <div className="kpi-item">
              <FontAwesomeIcon icon={faUsers} className="kpi-icon" />
              <div className="kpi-label">Employés actifs</div>
              <div className="kpi-value">{stats.totalEmployes}</div>
            </div>
            <div className="kpi-item">
              <FontAwesomeIcon icon={faCalendarCheck} className="kpi-icon" />
              <div className="kpi-label">Total présences</div>
              <div className="kpi-value">{stats.totalPresences}</div>
            </div>
            <div className="kpi-item">
              <FontAwesomeIcon icon={faClock} className="kpi-icon" />
              <div className="kpi-label">Jours analysés</div>
              <div className="kpi-value">{stats.days}</div>
              <div className="kpi-sub">Période sélectionnée</div>
            </div>
            <div className="kpi-item">
              <FontAwesomeIcon icon={faChartLine} className="kpi-icon" />
              <div className="kpi-label">Taux de présence</div>
              <div className="kpi-value">{stats.presenceRate}%</div>
            </div>
          </div>
        </div>

        {/* Graphiques principaux */}
        <div className="glass-card">
          <div className="charts-grid">
            <div className="chart-box">
              <div className="text-secondary mb-2"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Répartition par statut</div>
              <Doughnut data={donutData} options={donutOptions} />
            </div>
            <div className="chart-box">
              <div className="text-secondary mb-2"><FontAwesomeIcon icon={faChartLine} className="me-1" /> Évolution quotidienne</div>
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
        </div>

        {/* Graphique par service */}
        {stats.byService && stats.byService.length > 0 && (
          <div className="glass-card">
            <h5 className="mb-3 fw-bold" style={{ color: 'var(--accent-blue)' }}>
              <FontAwesomeIcon icon={faBuilding} className="me-2" /> Répartition par service
            </h5>
            <div style={{ height: '200px' }}>
              <Bar data={serviceData} options={serviceOptions} />
            </div>
          </div>
        )}

        {/* Barres de progression */}
        <div className="glass-card">
          <h5 className="mb-3 fw-bold" style={{ color: 'var(--accent-blue)' }}>
            <FontAwesomeIcon icon={faChartLine} className="me-2" /> Indicateurs de performance
          </h5>
          <div className="progress-section">
            <div className="progress-item">
              <div className="progress-header">
                <span><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Présence</span>
                <span>{stats.presenceRate}%</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill blue" style={{ width: `${Math.min(stats.presenceRate, 100)}%` }}></div>
              </div>
            </div>
            <div className="progress-item">
              <div className="progress-header">
                <span><FontAwesomeIcon icon={faClock} className="me-1" /> Retard</span>
                <span>{stats.rateRetard}%</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill orange" style={{ width: `${Math.min(stats.rateRetard, 100)}%` }}></div>
              </div>
            </div>
            <div className="progress-item">
              <div className="progress-header">
                <span><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" /> Absentéisme</span>
                <span>{stats.rateAbsent}%</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill red" style={{ width: `${Math.min(stats.rateAbsent, 100)}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PresenceDashboard;